import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Edit, Trash2, RotateCcw, Archive, X, Check, Loader2, Trash, Upload, Eye, BookOpen, AlertTriangle, CheckCircle, Building2, Award } from 'lucide-react';
import { supabase } from '../../supabase/client';
import { categoryStore, Organization, CourseCategory, Level } from '../../store/categoryStore';
import { formatDateTimeWithSeconds } from '../../utils/timeUtils';
import SearchWithHistory from '../../components/SearchWithHistory';
import QuizAnswerMode from '../quiz/QuizAnswerMode';
import { mapDbQuestionsToStandard } from '../../utils/questionMapper';

interface Paper {
  id: string;
  title: string;
  year: number | null;
  month: number | null;
  organization: string | null;
  category_type: string | null;
  level_text: string | null;
  paper_type: string | null;
  duration: number | null;
  question_count: number | null;
  single_choice_count: number | null;
  multi_choice_count: number | null;
  judge_count: number | null;
  coding_count: number | null;
  total_score: number | null;
  remark: string | null;
  is_free: boolean;
  is_published: boolean;
  status: string;
  created_at: string;
  updated_at: string | null;
  user_id: string | null;
}

const FILTER_STORAGE_KEY = 'pending_papers_filter_state';

interface FilterState {
  selectedOrganization: string;
  selectedCategory: string;
  selectedLevel: string;
  accessFilter: 'all' | 'free' | 'paid';
  searchQuery: string;
}

// 从 localStorage 读取初始筛选状态（用于 useState 初始化函数）
function loadSavedFilterState(): FilterState {
  try {
    const savedState = localStorage.getItem(FILTER_STORAGE_KEY);
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (e) {
    // 忽略解析错误
  }
  return {
    selectedOrganization: '',
    selectedCategory: '',
    selectedLevel: '',
    accessFilter: 'all',
    searchQuery: ''
  };
}

export default function PendingPapers() {
  const navigate = useNavigate();
  const savedState = loadSavedFilterState();
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState(savedState.searchQuery);
  const [accessFilter, setAccessFilter] = useState<'all' | 'free' | 'paid'>(savedState.accessFilter);
  const [selectedPaper, setSelectedPaper] = useState<Paper | null>(null);
  const [editingRemark, setEditingRemark] = useState<string | null>(null);
  const [remarkValue, setRemarkValue] = useState('');
  const [savingRemark, setSavingRemark] = useState(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);
  const [showBatchClearRemarkModal, setShowBatchClearRemarkModal] = useState(false);
  const [clearingBatchRemark, setClearingBatchRemark] = useState(false);
  const [showBatchSyncModal, setShowBatchSyncModal] = useState(false);
  const [syncingBatch, setSyncingBatch] = useState(false);
  const [showBatchPublishModal, setShowBatchPublishModal] = useState(false);
  const [publishingBatch, setPublishingBatch] = useState(false);

  // 预览模态框相关状态
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPaperId, setPreviewPaperId] = useState<string | null>(null);
  const [previewPaperData, setPreviewPaperData] = useState<any>(null);
  const [previewQuestions, setPreviewQuestions] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // 筛选状态
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>(savedState.selectedOrganization);
  const [selectedCategory, setSelectedCategory] = useState<string>(savedState.selectedCategory);
  const [selectedLevel, setSelectedLevel] = useState<string>(savedState.selectedLevel);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // 保存筛选状态到 localStorage（每次筛选条件变化时）
  useEffect(() => {
    const state: FilterState = {
      selectedOrganization,
      selectedCategory,
      selectedLevel,
      accessFilter,
      searchQuery
    };
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state));
  }, [selectedOrganization, selectedCategory, selectedLevel, accessFilter, searchQuery]);

  useEffect(() => {
    loadPapers();
    loadFilterData();
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedPaper(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const loadFilterData = async () => {
    setLoadingFilters(true);
    try {
      await categoryStore.init();
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    } catch (err) {

    } finally {
      setLoadingFilters(false);
    }
  };

  const filteredCategories = useMemo(() => {
    if (!selectedOrganization) return [];
    return categories.filter(c => c.orgId === selectedOrganization);
  }, [categories, selectedOrganization]);

  const filteredLevels = useMemo(() => {
    if (!selectedCategory) return [];
    return levels.filter(l => l.catId === selectedCategory);
  }, [levels, selectedCategory]);

  // 级联筛选校验：仅在 categories/levels 数据加载完成后执行
  useEffect(() => {
    if (selectedOrganization && categories.length > 0) {
      const cats = categories.filter(c => c.orgId === selectedOrganization);
      if (cats.length > 0 && selectedCategory && !cats.find(c => c.id === selectedCategory)) {
        setSelectedCategory('');
        setSelectedLevel('');
      }
    }
  }, [selectedOrganization, categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory && levels.length > 0) {
      const levs = levels.filter(l => l.catId === selectedCategory);
      if (levs.length > 0 && selectedLevel && !levs.find(l => l.id === selectedLevel)) {
        setSelectedLevel('');
      }
    }
  }, [selectedCategory, levels, selectedLevel]);

  const clearFilters = () => {
    setSelectedOrganization('');
    setSelectedCategory('');
    setSelectedLevel('');
    setAccessFilter('all');
    setSearchQuery('');
    // 清除 localStorage 中的筛选状态
    localStorage.removeItem(FILTER_STORAGE_KEY);
  };

  const loadPapers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('papers')
        .select('*')
        .eq('status', 'draft')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPapers((data || []) as unknown as Paper[]);
      setSelectedIds(new Set());
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredPapers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPapers.map(p => p.id)));
    }
  };

  const deletePaper = async (paper: Paper) => {
    if (!confirm('确定删除此试卷？删除后将移入回收站')) return;
    try {
      await supabase
        .from('papers')
        .update({
          deleted_at: new Date().toISOString(),
          original_status: paper.status,
          updated_at: new Date().toISOString()
        })
        .eq('id', paper.id);
      loadPapers();
    } catch (err) {
      alert('删除失败');
    }
  };

  const syncPaper = async (paper: Paper) => {
    if (!confirm('确定将此试卷转为正式试卷？转为正式后默认为隐藏状态，需在试卷列表手动发布后才能对外可见。')) return;
    try {
      await supabase.from('papers').update({ status: 'published', is_published: false }).eq('id', paper.id);
      loadPapers();
      alert('转为正式成功，试卷当前为隐藏状态，请在试卷列表手动发布');
    } catch (err) {
      
      alert('转为正式失败');
    }
  };

  const publishPaper = async (paper: Paper) => {
    if (!confirm('确定直接发布此试卷？\n\n发布后试卷将立即对外可见，学员可以参与考试。\n此操作不可逆，请确认试卷内容已完善。')) return;
    try {
      const { error } = await supabase
        .from('papers')
        .update({
          status: 'published',
          is_published: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', paper.id);

      if (error) throw error;

      await supabase
        .from('questions')
        .update({ status: 'published' })
        .eq('paper_id', paper.id);

      loadPapers();
      alert('发布成功！试卷已对外可见，学员可以参与考试');
    } catch (err) {
      
      alert('发布失败');
    }
  };

  const startEditRemark = (paper: Paper) => {
    setEditingRemark(paper.id);
    setRemarkValue(paper.remark || '');
  };

  const saveRemark = async (paperId: string) => {
    if (savingRemark) return;
    setSavingRemark(true);
    try {
      const { error } = await supabase
        .from('papers')
        .update({ remark: remarkValue, updated_at: new Date().toISOString() })
        .eq('id', paperId);
      if (error) throw error;
      setPapers(papers.map(p => p.id === paperId ? { ...p, remark: remarkValue } : p));
      setEditingRemark(null);
    } catch (err) {
      
      alert('保存失败');
    } finally {
      setSavingRemark(false);
    }
  };

  const handleRemarkKeyDown = (e: React.KeyboardEvent, paperId: string) => {
    if (e.key === 'Enter') {
      saveRemark(paperId);
    }
  };

  const generateDisplayTitle = (paper: Paper) => {
    if (paper.title) return paper.title;
    // 如果没有title，根据字段动态生成
    const parts: string[] = [];
    if (paper.year) parts.push(`${paper.year}年`);
    if (paper.month) parts.push(`${paper.month}月`);
    if (paper.organization) parts.push(paper.organization);
    if (paper.category_type) {
      const categoryName = categoryStore.getCategoryNameByCode(paper.category_type) || paper.category_type;
      parts.push(categoryName);
    }
    if (paper.level_text) parts.push(paper.level_text);
    if (paper.paper_type) {
      parts.push(paper.paper_type === 'real' ? '真题' : '模拟题');
    }
    return parts.length > 0 ? parts.join('') : '未命名试卷';
  };

  // 预览试卷 - 打开模态框并加载数据
  const handleView = async (paper: Paper) => {
    setPreviewPaperId(paper.id);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    try {
      // 加载试卷数据
      const { data: paperData } = await supabase.from('papers').select('*').eq('id', paper.id).single();
      if (paperData) {
        setPreviewPaperData(paperData);
        // 加载题目数据
        const { data: questionsData } = await supabase.from('questions').select('*').eq('paper_id', paper.id).order('order_index', { ascending: true });
        // 直接存储 DB 原始数据，由 mapDbQuestionsToStandard 统一映射
        setPreviewQuestions(questionsData || []);
      }
    } catch (error) {
      console.error('加载预览数据失败:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setAccessFilter('all');
  };

  const formatDate = (dateStr: string | null) => {
    return formatDateTimeWithSeconds(dateStr);
  };

  const getStatusTag = () => {
    return <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">草稿</span>;
  };

  const getAccessTag = (isFree: boolean) => {
    if (isFree) {
      return <span className="px-2 py-0.5 text-xs font-medium text-green-600 bg-green-50 rounded-full">免费</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-medium text-orange-600 bg-orange-50 rounded-full">付费</span>;
  };

  const resolveCategoryType = (raw: string | null): string | null => {
    if (!raw) return null;
    return categoryStore.getCategoryNameByCode(raw) || raw;
  };

  // 判断分类名称是否属于"非编程类"（other），用于筛选时把所有 other 类分类归并匹配
  // 规则：图形化→scratch、Python→python、C++/C语言→cpp，其余均为 other
  const isOtherCategoryByName = (name: string): boolean => {
    if (name.includes('图形化')) return false;
    if (name.includes('Python')) return false;
    if (name.includes('C++') || name.includes('C语言')) return false;
    return true;
  };

  const filteredPapers = papers.filter(p => {
    if (accessFilter !== 'all') {
      if (accessFilter === 'free' && !p.is_free) return false;
      if (accessFilter === 'paid' && p.is_free) return false;
    }
    const resolvedType = resolveCategoryType(p.category_type);
    // 试卷是否属于 other 类型（category_type 为 'other' 或解析后仍为 'other'）
    const paperIsOther = p.category_type === 'other' || resolvedType === 'other';
    if (selectedOrganization) {
      const cat = categories.find(c => c.name === resolvedType || c.id === p.category_type);
      // other 类试卷：只要选中组织下存在任意 other 类分类即视为匹配
      if ((!cat || cat.orgId !== selectedOrganization) && paperIsOther) {
        const orgHasOther = categories.some(c => c.orgId === selectedOrganization && isOtherCategoryByName(c.name));
        if (orgHasOther) {
          // 继续后续 selectedCategory 判断
        } else {
          return false;
        }
      } else if (!cat || cat.orgId !== selectedOrganization) {
        return false;
      }
    }
    if (selectedCategory) {
      const selectedCat = categories.find(c => c.id === selectedCategory);
      if (!selectedCat) return false;
      // 精确匹配：分类名或 id 直接相等
      const exactMatch = resolvedType === selectedCat.name || p.category_type === selectedCategory;
      if (exactMatch) {
        // 命中精确匹配
      } else if (paperIsOther && isOtherCategoryByName(selectedCat.name)) {
        // other 类试卷 + 选中的也是 other 类分类 → 视为匹配
      } else {
        return false;
      }
    }
    if (selectedLevel) {
      const level = levels.find(l => l.id === selectedLevel);
      if (!level || p.level_text !== level.name) return false;
    }
    if (!searchQuery.trim()) return true;
    const searchTerms = searchQuery.trim().toLowerCase().split(/\s+/).filter(term => term.length > 0);
    if (searchTerms.length === 0) return true;
    const title = (p.title || '').toLowerCase();
    const remark = (p.remark || '').toLowerCase();
    return searchTerms.every(term => title.includes(term) || remark.includes(term));
  });

  return (
    <div className="p-6 bg-[#f0f7ff] min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">草稿列表</h1>
          <p className="text-gray-500 mt-1">管理草稿试卷</p>
        </div>
      </div>

      {/* 三级级联筛选 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="space-y-4">
          {/* 考评体系 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">考评体系</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {loadingFilters ? (
                <div className="px-4 py-2 text-sm text-gray-400">加载中...</div>
              ) : organizations.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无考评体系</div>
              ) : (
                organizations.map(org => (
                  <motion.button
                    key={org.id}
                    onClick={() => { setSelectedOrganization(org.id); setSelectedCategory(''); setSelectedLevel(''); }}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedOrganization === org.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {org.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>

          {/* 课程分类 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">课程分类</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!selectedOrganization ? (
                <div className="px-4 py-2 text-sm text-gray-400">请先选择考评体系</div>
              ) : filteredCategories.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无课程分类</div>
              ) : (
                filteredCategories.map(cat => (
                  <motion.button
                    key={cat.id}
                    onClick={() => { setSelectedCategory(cat.id); setSelectedLevel(''); }}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedCategory === cat.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {cat.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>

          {/* 级别 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">级别</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!selectedCategory ? (
                <div className="px-4 py-2 text-sm text-gray-400">请先选择课程分类</div>
              ) : filteredLevels.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无级别</div>
              ) : (
                filteredLevels.map(level => (
                  <motion.button
                    key={level.id}
                    onClick={() => setSelectedLevel(level.id)}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedLevel === level.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {level.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>

          {/* 清空筛选 */}
          {(selectedOrganization || selectedCategory || selectedLevel) && (
            <div className="pt-2 border-t border-gray-100">
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                清空筛选条件
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <SearchWithHistory
          storageKey="pending_papers_search_history"
          placeholder="搜索试卷名称或备注（支持空格分隔多条件）"
          value={searchQuery}
          onChange={setSearchQuery}
          onSearch={() => {}}
          onReset={resetFilters}
        />
        <select
          value={accessFilter}
          onChange={(e) => setAccessFilter(e.target.value as 'all' | 'free' | 'paid')}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">全部</option>
          <option value="paid">付费</option>
          <option value="free">免费</option>
        </select>
        {selectedIds.size > 0 && (
          <>
            <button
              onClick={() => setShowBatchSyncModal(true)}
              className="px-4 py-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              批量转为正式 ({selectedIds.size})
            </button>
            <button
              onClick={() => setShowBatchPublishModal(true)}
              className="px-4 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              批量直接发布 ({selectedIds.size})
            </button>
            <button
              onClick={() => setShowBatchClearRemarkModal(true)}
              className="px-4 py-2 text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              批量清除备注 ({selectedIds.size})
            </button>
            <button
              onClick={() => setShowBatchDeleteModal(true)}
              className="px-4 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 flex items-center gap-2"
            >
              <Trash className="w-4 h-4" />
              批量删除 ({selectedIds.size})
            </button>
          </>
        )}
        <button
          onClick={resetFilters}
          className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          重置筛选
        </button>
        <button
          onClick={() => navigate('/admin/recycle-bin')}
          className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <Archive className="w-4 h-4" />
          回收站
        </button>
      </div>

      {!loading && (
        <div className="mb-3 text-sm text-gray-600">
          {searchQuery.trim() ? (
            filteredPapers.length === 0 ? (
              <span>未找到匹配的试卷</span>
            ) : (
              <span>共找到 <span className="font-medium text-blue-600">{filteredPapers.length}</span> 份试卷草稿</span>
            )
          ) : (
            <span>共 <span className="font-medium text-gray-900">{filteredPapers.length}</span> 份试卷草稿</span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : filteredPapers.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>{searchQuery.trim() ? '未找到匹配的试卷' : '暂无试卷草稿'}</p>
          {searchQuery.trim() && (
            <p className="text-sm text-gray-400 mt-2">尝试使用其他关键词或空格分隔多个条件</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#e8f4ff]">
              <tr>
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === papers.length && papers.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-blue-900">试卷名称</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">备注</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">单选</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">多选</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">判断</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">非客观</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">总分</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">时长</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">状态</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">访问</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">最后更新</th>
                <th className="px-3 py-3 text-center text-sm font-medium text-blue-900">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPapers.map((paper) => (
                <motion.tr
                  key={paper.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(paper.id)}
                      onChange={() => toggleSelect(paper.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => setSelectedPaper(paper)}
                        className="text-sm text-gray-900 font-medium hover:text-blue-600 hover:underline cursor-pointer text-left"
                      >
                        {generateDisplayTitle(paper)}
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-center">
                    {editingRemark === paper.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={remarkValue}
                          onChange={(e) => setRemarkValue(e.target.value)}
                          onBlur={() => saveRemark(paper.id)}
                          onKeyDown={(e) => handleRemarkKeyDown(e, paper.id)}
                          className="w-24 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                        {remarkValue && (
                          <button
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setRemarkValue('');
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="清除备注"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {savingRemark ? (
                          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    ) : (
                      <span
                        onClick={() => startEditRemark(paper)}
                        className="text-sm text-gray-600 cursor-pointer hover:text-blue-600 hover:underline"
                      >
                        {paper.remark || '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.single_choice_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.multi_choice_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.judge_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.coding_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.total_score || '-'}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.duration ? `${paper.duration}分钟` : '-'}</td>
                  <td className="px-2 py-3 text-center">{getStatusTag()}</td>
                  <td className="px-2 py-3 text-center">{getAccessTag(paper.is_free)}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{formatDate(paper.updated_at)}</td>
                  <td className="px-3 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => handleView(paper)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                        title="预览"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => syncPaper(paper)}
                        className="px-2 py-1 text-xs font-medium text-white bg-green-500 hover:bg-green-600 rounded"
                        title="转为正式"
                      >
                        转为正式
                      </button>
                      <button
                        onClick={() => publishPaper(paper)}
                        className="px-2 py-1 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded flex items-center gap-1"
                        title="直接发布"
                      >
                        <Upload className="w-3 h-3" />
                        直接发布
                      </button>
                      <button
                        onClick={() => navigate(`/admin/paper-editor?edit=${paper.id}&from=pending`)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePaper(paper)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {showBatchSyncModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !syncingBatch && setShowBatchSyncModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-full">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">确认批量转为正式</h2>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要将选中的 <span className="font-semibold text-green-600">{selectedIds.size}</span> 个草稿试卷转为正式吗？
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  转为正式后试卷默认为隐藏状态，需在试卷列表手动发布后才能对外可见。
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBatchSyncModal(false)}
                    disabled={syncingBatch}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      setSyncingBatch(true);
                      try {
                        const now = new Date().toISOString();
                        for (const id of selectedIds) {
                          await supabase
                            .from('papers')
                            .update({
                              status: 'published',
                              is_published: false,
                              updated_at: now
                            })
                            .eq('id', id);
                        }
                        setSelectedIds(new Set());
                        setShowBatchSyncModal(false);
                        loadPapers();
                        alert('批量转为正式成功，试卷当前为隐藏状态，请在试卷列表手动发布');
                      } catch (err) {
                        
                        alert('批量转为正式失败');
                      } finally {
                        setSyncingBatch(false);
                      }
                    }}
                    disabled={syncingBatch}
                    className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {syncingBatch ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        处理中...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        确认转为正式
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBatchPublishModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !publishingBatch && setShowBatchPublishModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-red-100 rounded-full">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">确认批量直接发布</h2>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要直接发布选中的 <span className="font-semibold text-red-600">{selectedIds.size}</span> 个草稿试卷吗？
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  发布后试卷将立即对外可见，学员可以参与考试。此操作不可逆，请确认试卷内容已完善。
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBatchPublishModal(false)}
                    disabled={publishingBatch}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      setPublishingBatch(true);
                      try {
                        const now = new Date().toISOString();
                        for (const id of selectedIds) {
                          await supabase
                            .from('papers')
                            .update({
                              status: 'published',
                              is_published: true,
                              updated_at: now
                            })
                            .eq('id', id);
                          await supabase
                            .from('questions')
                            .update({ status: 'published' })
                            .eq('paper_id', id);
                        }
                        setSelectedIds(new Set());
                        setShowBatchPublishModal(false);
                        loadPapers();
                        alert('批量发布成功！试卷已对外可见，学员可以参与考试');
                      } catch (err) {
                        
                        alert('批量发布失败');
                      } finally {
                        setPublishingBatch(false);
                      }
                    }}
                    disabled={publishingBatch}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {publishingBatch ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        发布中...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        确认发布
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBatchClearRemarkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !clearingBatchRemark && setShowBatchClearRemarkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-orange-100 rounded-full">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">确认批量清除备注</h2>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要清除选中的 <span className="font-semibold text-orange-600">{selectedIds.size}</span> 个试卷的备注吗？
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  此操作将清空这些试卷的备注内容，清除后无法恢复。
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBatchClearRemarkModal(false)}
                    disabled={clearingBatchRemark}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      setClearingBatchRemark(true);
                      try {
                        const now = new Date().toISOString();
                        for (const id of selectedIds) {
                          await supabase
                            .from('papers')
                            .update({
                              remark: '',
                              updated_at: now
                            })
                            .eq('id', id);
                        }
                        setSelectedIds(new Set());
                        setShowBatchClearRemarkModal(false);
                        loadPapers();
                        alert('批量清除备注成功');
                      } catch (err) {
                        
                        alert('批量清除备注失败');
                      } finally {
                        setClearingBatchRemark(false);
                      }
                    }}
                    disabled={clearingBatchRemark}
                    className="px-4 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {clearingBatchRemark ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        清除中...
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        确认清除
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBatchDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !deletingBatch && setShowBatchDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-red-100 rounded-full">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">确认批量删除</h2>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要删除选中的 <span className="font-semibold text-red-600">{selectedIds.size}</span> 个草稿试卷吗？
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  删除后将移入回收站，您可以在回收站中恢复这些试卷。
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBatchDeleteModal(false)}
                    disabled={deletingBatch}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      setDeletingBatch(true);
                      try {
                        const now = new Date().toISOString();
                        for (const id of selectedIds) {
                          const paper = papers.find(p => p.id === id);
                          await supabase
                            .from('papers')
                            .update({
                              deleted_at: now,
                              original_status: paper?.status || 'draft',
                              updated_at: now
                            })
                            .eq('id', id);
                        }
                        setSelectedIds(new Set());
                        setShowBatchDeleteModal(false);
                        loadPapers();
                        alert('批量删除成功，已移入回收站');
                      } catch (err) {
                        
                        alert('批量删除失败');
                      } finally {
                        setDeletingBatch(false);
                      }
                    }}
                    disabled={deletingBatch}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {deletingBatch ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        删除中...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        确认删除
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedPaper && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPaper(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">试卷详情</h2>
                  <button
                    onClick={() => setSelectedPaper(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">试卷名称</h3>
                    <p className="text-base text-gray-900">{generateDisplayTitle(selectedPaper)}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">年份</h3>
                      <p className="text-base text-gray-900">{selectedPaper.year || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">月份</h3>
                      <p className="text-base text-gray-900">{selectedPaper.month || '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">考评体系</h3>
                      <p className="text-base text-gray-900">{selectedPaper.organization || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">课程分类</h3>
                      <p className="text-base text-gray-900">{selectedPaper.category_type || '-'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">级别分类</h3>
                      <p className="text-base text-gray-900">{selectedPaper.level_text || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">类型</h3>
                      <p className="text-base text-gray-900">
                        {selectedPaper.paper_type === 'real' ? '真题' : selectedPaper.paper_type === 'mock' ? '模拟题' : '-'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">单选题</h3>
                      <p className="text-lg font-semibold text-gray-900">{selectedPaper.single_choice_count || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">多选题</h3>
                      <p className="text-lg font-semibold text-gray-900">{selectedPaper.multi_choice_count || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">判断题</h3>
                      <p className="text-lg font-semibold text-gray-900">{selectedPaper.judge_count || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">非客观</h3>
                      <p className="text-lg font-semibold text-gray-900">{selectedPaper.coding_count || 0}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">总分</h3>
                      <p className="text-base text-gray-900">{selectedPaper.total_score || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">时长</h3>
                      <p className="text-base text-gray-900">{selectedPaper.duration ? `${selectedPaper.duration}分钟` : '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">题目总数</h3>
                      <p className="text-base text-gray-900">{selectedPaper.question_count || 0}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">状态</h3>
                      <div className="mt-1">{getStatusTag()}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">访问权限</h3>
                      <div className="mt-1">{getAccessTag(selectedPaper.is_free)}</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">备注</h3>
                    <p className="text-base text-gray-900">{selectedPaper.remark || '-'}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">制卷人</h3>
                      <p className="text-base text-gray-900">-</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">制卷日期</h3>
                      <p className="text-base text-gray-900">{formatDate(selectedPaper.created_at)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">修改记录</h3>
                      <p className="text-base text-gray-900">-</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">最后更新时间</h3>
                      <p className="text-base text-gray-900">{formatDate(selectedPaper.updated_at)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setSelectedPaper(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 试卷预览模态框 */}
      <AnimatePresence>
        {showPreviewModal && previewPaperData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => setShowPreviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <QuizAnswerMode
                  paper={{
                    id: previewPaperId || '',
                    title: previewPaperData.title || '试卷预览',
                    total_score: previewPaperData.total_score || 0,
                    category_type: previewPaperData.category_type || '',
                    description: previewPaperData.description || '',
                    remark: previewPaperData.remark || '',
                    year: previewPaperData.year,
                    month: previewPaperData.month,
                    organization: previewPaperData.organization,
                    source: previewPaperData.source,
                    nature: previewPaperData.nature,
                    level_text: previewPaperData.level_text,
                    paper_type: previewPaperData.paper_type,
                    duration: previewPaperData.duration || 60
                  }}
                  examQuestions={mapDbQuestionsToStandard(previewQuestions)}
                  currentUser={null}
                  paperId={previewPaperId || undefined}
                  isPreview={true}
                  isEditorPreview={true}
                  onBack={() => setShowPreviewModal(false)}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
